import React from "react";
import { formatDate, formatNumber } from "@/lib/utils";

interface WarehouseInventoryPrintTemplateProps {
  data: any[];
  config: any;
  typeLabel?: string; // "Báo cáo tồn kho vật tư", "Báo cáo tồn kho thành phẩm", etc.
}

export default function WarehouseInventoryPrintTemplate({
  data,
  config,
  typeLabel,
}: WarehouseInventoryPrintTemplateProps) {
  if (!data || data.length === 0) return null;

  const title = typeLabel || "BÁO CÁO TỒN KHO";
  
  // Group logic if needed, here just a simple sum
  const totalQty = data.reduce((sum, item) => sum + (Number(item.qtyOnHand) || 0), 0);
  
  return (
    <div className="print-content text-[13px] leading-relaxed text-slate-900 w-full font-serif bg-white">
      {/* Header */}
      <div className="flex items-start justify-between border-b pb-4 mb-6 border-slate-800">
        <div className="flex-1 pr-4">
          <h2 className="text-xl font-bold uppercase mb-1">
            {config?.companyName || "CÔNG TY CP IN VÀ BAO BÌ APV"}
          </h2>
          <p>
            <span className="font-semibold">Địa chỉ:</span>{" "}
            {config?.companyAddress || "Số 1, Đường 1, KCN Tân Bình, TP.HCM"}
          </p>
          <p>
            <span className="font-semibold">Mã số thuế:</span>{" "}
            {config?.taxId || "0123456789"}
          </p>
        </div>
        <div className="text-right flex flex-col items-end">
           <div className="w-[120px] h-[60px] flex-shrink-0 flex items-center justify-end mb-2">
            {config?.logoUrl ? (
                <img
                src={config.logoUrl}
                alt="Logo"
                className="max-w-full max-h-full object-contain grayscale"
                />
            ) : (
                <div className="w-16 h-16 bg-slate-100 border border-slate-300 flex items-center justify-center font-bold text-slate-400">
                LOGO
                </div>
            )}
           </div>
           <p className="italic">Ngày in: {formatDate(new Date())}</p>
        </div>
      </div>

      {/* Title */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold uppercase tracking-wide">
          {title}
        </h1>
      </div>

      {/* Table */}
      <table className="w-full border-collapse border border-slate-800 mb-8">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-800 px-2 py-1.5 text-center w-[50px]">
              STT
            </th>
            <th className="border border-slate-800 px-2 py-1.5 text-left w-[120px]">
              Mã
            </th>
            <th className="border border-slate-800 px-2 py-1.5 text-left">
              Tên / Mô tả
            </th>
            <th className="border border-slate-800 px-2 py-1.5 text-left w-[120px]">
              Phân loại
            </th>
            <th className="border border-slate-800 px-2 py-1.5 text-center w-[60px]">
              ĐVT
            </th>
            <th className="border border-slate-800 px-2 py-1.5 text-right w-[100px]">
              Tồn kho
            </th>
            <th className="border border-slate-800 px-2 py-1.5 text-left w-[100px]">
              Vị trí
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((item: any, idx: number) => {
            const itemCode = 
              item.material?.code || 
              item.materialItem?.code ||
              item.fg?.code || 
              item.product?.code ||
              item.tooling?.code || 
              item.toolingItem?.code || 
              item.toolingId || 
              "";
            const itemName = 
              item.material?.name || 
              item.materialItem?.name ||
              item.fg?.name || 
              item.product?.name ||
              item.tooling?.name || 
              item.toolingItem?.name || 
              item.toolingName ||
              "";
            const itemUnit = 
              item.material?.unit || 
              item.materialItem?.unit ||
              item.fg?.unit || 
              item.product?.unit ||
              item.tooling?.unit || 
              item.toolingItem?.unit || 
              item.unit ||
              "";
            
            const category = 
              item.materialItem?.category?.name || 
              item.material?.categoryId || 
              item.fg?.categoryId || 
              item.toolingItem?.category ||
              item.category || 
              "—";

            return (
              <tr key={item.id || idx}>
                <td className="border border-slate-800 px-2 py-1.5 text-center">
                  {idx + 1}
                </td>
                <td className="border border-slate-800 px-2 py-1.5 font-mono text-[11px] font-bold">
                  {itemCode}
                </td>
                <td className="border border-slate-800 px-2 py-1.5">
                  {itemName}
                </td>
                <td className="border border-slate-800 px-2 py-1.5">
                  {category}
                </td>
                <td className="border border-slate-800 px-2 py-1.5 text-center">
                  {itemUnit}
                </td>
                <td className="border border-slate-800 px-2 py-1.5 text-right font-semibold">
                  {formatNumber(item.qtyOnHand)}
                </td>
                <td className="border border-slate-800 px-2 py-1.5 text-left text-[11px] font-mono">
                  {item.location || "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
            <tr className="bg-slate-50 font-bold">
                <td colSpan={5} className="border border-slate-800 px-2 py-2 text-right uppercase">
                    Tổng cộng:
                </td>
                <td className="border border-slate-800 px-2 py-2 text-right">
                    {formatNumber(totalQty)}
                </td>
                <td className="border border-slate-800 px-2 py-2"></td>
            </tr>
        </tfoot>
      </table>

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-4 text-center mt-12">
        <div>
          <p className="font-semibold mb-1">Người lập báo cáo</p>
          <p className="italic text-xs text-slate-500 mb-20">(Ký, họ tên)</p>
        </div>
        <div>
          <p className="font-semibold mb-1">Thủ kho / Quản lý</p>
          <p className="italic text-xs text-slate-500 mb-20">(Ký, họ tên)</p>
        </div>
      </div>
    </div>
  );
}
